/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX, ReactNode } from 'react';
import{
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Button, Divider, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import type { AnyAction, Dispatch } from 'redux';
import styled from 'styled-components';

import { withAPIService } from '../../networking';
import { FINANCIAL_CARD_CATEGORIES } from '../../shared/cardConfig/constants';
import { withStore } from '../../store';
import {
  closeEditMode,
  toggleEditMode
} from '../../store/editMode';

import './ReviewHeader.css';

const actionButtonBaseSx = {
  width: { xs: '100%', md: 'auto' },
  maxWidth: '100%',
  height: '40px !important'
};

const Wrapper = styled.header`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;

  @media only screen and (max-width: 700px) {
    flex-direction: column;
  }
`;

const TitleWrapper = styled.div`
  width: 80%;

  @media only screen and (max-width: 700px) {
    width: 100%;
  }
`;

const MobileActions = styled.div`
  display: none;

  @media only screen and (max-width: 700px) {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-bottom: 16px;
  }
`;

const DesktopActions = styled.div`
  display: flex;

  @media only screen and (max-width: 700px) {
    display: none;
  }
`;

export const getError = (
  fieldWrapper: any,
  errors: any,
  errorMustContain: any,
  cardId: string
): boolean => {
  if (fieldWrapper && fieldWrapper in errors) {
    if (cardId === 'shipTo') {
      cardId = 'address';
    }
    return cardId in errors[fieldWrapper][0];
  }

  if (errorMustContain && cardId in errors) {
    return errorMustContain in errors[cardId];
  }

  return cardId in errors;
};


interface ReviewHeaderProps {
  title?: string;
  cardId: string;
  fieldWrapper?: any;
  errorMustContain?: any;
  store: any;
  dispatch: Dispatch<AnyAction>;
  apiService: any;
  setMobileFooterDone?: (node: ReactNode | null) => void;
}

function Component({
  title,
  cardId,
  fieldWrapper,
  errorMustContain,
  store,
  dispatch,
  apiService,
  setMobileFooterDone
}: ReviewHeaderProps): JSX.Element {
  const { errors } = useFormikContext<any>();
  const { locked } = store.attachments;
  const { t } = useTranslation();

  const hideSaveEdit =
    cardId === 'signature' ||
    (locked && cardId in FINANCIAL_CARD_CATEGORIES);

  const withError = getError(
    fieldWrapper,
    errors,
    errorMustContain,
    cardId
  );

  const editMode = (id: string) =>
    id === 'address'
      ? store.editMode['shipTo']
      : store.editMode[id];

  function handleClick(id: string) {
    if (id === 'address') {
      id = 'shipTo';
    }
    dispatch(toggleEditMode(id));
  }

  const handleSave = useCallback(
    (id: string) => {
      const normalizedId =
        id === 'address' ? 'shipTo' : id;

      apiService
        .updateCustomer(store.customer, dispatch)
        .then(() =>
          dispatch(closeEditMode(normalizedId))
        );
    },
    [apiService, store.customer, dispatch]
  );

  const shouldShowActions = !hideSaveEdit;
  const isEditing = !!editMode(cardId) || withError;

  const doneButton = useMemo(
    () => (
      <Button
        type="button"
        disabled={withError}
        onClick={() => handleSave(cardId)}
        variant="contained"
        startIcon={<CheckIcon />}
        sx={theme => ({
          ...theme.unstable_sx({
            padding: '8px 16px',
            '& .MuiButton-startIcon': {
              color: '#ffffff'
            }
          }),
          ...actionButtonBaseSx
        })}
      >
        {t('buttons.done')}
      </Button>
    ),
    [withError, handleSave, t, cardId]
  );

  useEffect(() => {
    if (typeof setMobileFooterDone !== 'function') return;

    const footerDone =
      shouldShowActions && isEditing
        ? doneButton
        : null;

    setMobileFooterDone(footerDone);
    return () => setMobileFooterDone(null);
  }, [
    setMobileFooterDone,
    shouldShowActions,
    isEditing,
    doneButton
  ]);

  const editButtonDesktop = (
    <Button
      type="button"
      onClick={() => handleClick(cardId)}
      variant="outlined"
      size="medium"
      startIcon={<EditOutlinedIcon />}
      sx={theme =>
        theme.unstable_sx({
          padding: '8px 16px',
          height: '40px !important',
          '& .MuiButton-startIcon': {
            color: '#00843D'
          }
        })
      }
    >
      {t('buttons.edit')}
    </Button>
  );

  const editButtonMobile = (
    <Button
      type="button"
      onClick={() => handleClick(cardId)}
      variant="text"
      size="medium"
      startIcon={
        <EditOutlinedIcon
          sx={theme => ({
            color: '#00843D',
            transition: 'color 0.2s',
            '.MuiButton-root:hover &': {
              color:
                theme.palette.success?.dark ??
                '#03441F'
            },
            '.Mui-disabled &': {
              color: '#BDBDBD'
            }
          })}
        />
      }
      sx={{ padding: '8px 16px' }}
    >
      {t('buttons.edit')}
    </Button>
  );

  
let desktopAction = null;
if (shouldShowActions) {
  if (isEditing) {
    desktopAction = doneButton;
  } else {
    desktopAction = editButtonDesktop;
  }
}


  return (
    <Wrapper>
      <Header>
        <TitleWrapper>
          <Typography
            className="reviewHeaderTitle"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '18px', md: '20px' },
              lineHeight: { xs: '24px', md: '26px' }
            }}
          >
            {title}
          </Typography>
        </TitleWrapper>

        <DesktopActions>
          {desktopAction}
        </DesktopActions>
      </Header>

      <Divider
        sx={{
          marginTop: 'var(--spacing-lg, 1rem)',
          marginBottom:
            'var(--spacing-lg, 1rem)',
          borderColor:
            'var(--border-primary, #DBDDDC)'
        }}
      />

      {shouldShowActions && !isEditing && (
        <MobileActions>
          {editButtonMobile}
        </MobileActions>
      )}
    </Wrapper>
  );
}

const connectAPI = withAPIService(Component);
const ReviewHeader = withStore(connectAPI);

export default ReviewHeader;